import { useId, type ReactElement } from 'react';

type TransportIcon = 'play' | 'pause' | 'skip';

/**
 * Drawn rather than pulled from an icon set: three shapes, all of them straight lines and
 * triangles, is less to carry than a dependency and keeps them on the theme's `currentColor`.
 */
const ICON_PATHS: Readonly<Record<TransportIcon, string>> = {
  play: 'M5 3.5v13l11-6.5z',
  pause: 'M6 4h3v12H6zM11 4h3v12h-3z',
  skip: 'M5 3.5v13l9-6.5zM15 4h2v12h-2z',
};

type TransportButtonProps = {
  readonly icon: TransportIcon;
  /** Names the action for a screen reader and is what the tooltip shows. */
  readonly label: string;
  readonly isDisabled: boolean;
  readonly onPress: () => void;
};

/**
 * A console control small enough to sit under the sleeve without crowding it. The label is not
 * optional and is not decoration: an unlabelled triangle beside another triangle is a guess, so
 * it is both the accessible name and the tooltip, and the tooltip answers to focus as well as
 * hover — a DJ on a laptop tabbing through the console gets the same help as one with a mouse.
 */
export const TransportButton = ({
  icon,
  label,
  isDisabled,
  onPress,
}: TransportButtonProps): ReactElement => {
  const tipId = useId();

  return (
    <span className="group relative inline-flex">
      <button
        type="button"
        aria-label={label}
        aria-describedby={tipId}
        disabled={isDisabled}
        onClick={onPress}
        className="border-line text-content hover:bg-surface-hover focus:outline-accent-ring flex size-9 items-center justify-center rounded-pill border focus:outline-2 focus:outline-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
      >
        <svg viewBox="0 0 20 20" aria-hidden="true" className="size-4 fill-current">
          <path d={ICON_PATHS[icon]} />
        </svg>
      </button>
      <span
        id={tipId}
        role="tooltip"
        className="bg-surface border-subtle shadow-raised rounded-card text-secondary pointer-events-none absolute bottom-full left-1/2 z-20 mb-2 w-max max-w-48 -translate-x-1/2 border px-2.5 py-1.5 text-center text-xs opacity-0 transition-opacity group-hover:opacity-100 group-focus-within:opacity-100"
      >
        {label}
      </span>
    </span>
  );
};
